import type { Context } from '@/@types/ssr';
import type { WebLocale } from '@/enums/web-locale';
import type { Route } from 'vue-router';
import type { CreateElement } from 'vue';

import Vue from 'vue';

import App from '@/App.vue';

import { getApolloProvider } from '@/graphql/apollo';
import { createI18n } from '@/i18n';

import LocalForage from 'localforage';
import Cookies from 'js-cookie';

import { activeSubgenreCountriesWebLocales } from '@/constants/subgenres';
import { initializeApp } from 'firebase/app';
import { initArbitrage } from '@/helpers/composables/useArbitrage';
import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';

// plugins & filters
import { GlobalComponentsPlugin } from '@/plugins/global-components';
import { GlobalDirectivesPlugin } from '@/plugins/global-directives';
import { PiniaVuePlugin } from 'pinia';
import VueFinalModal from 'vue-final-modal';
import VueMeta from 'vue-meta';

// global filters & components
import { createThumborFilter } from '@/filters/thumbor';
// @ts-expect-error Could not find a declaration file for module 'vue-lazy-hydration'.
import LazyHydrate from 'vue-lazy-hydration';
// @ts-expect-error Could not find a declaration file for module 'vue-no-ssr'.
import NoSSR from 'vue-no-ssr';
import AppContent from '@/components/AppContent.vue';
import AppFooter from '@/components/footer/AppFooter.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';

// utils/helpers
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { generateJwId, generateUUIDv4 } from '@/helpers/uuid-helper';
import { initVfm } from '@/helpers/vfm/vfm-helper';
import { storeNoIndexDebug } from '@/helpers/query-parameters-helper';
import { storeSrDebuggerInfo } from '@/features/sponsored-recommendations/helpers/sponsoredrec-debugging-tools';
import { storeUserGeoLocation } from '@/helpers/geo-location-helper';
import { withRetry } from '@/helpers/retry-helper';

//routing
import { createRouter } from '@/routing';
import { globalRoutes } from '@/routing/global';
import { applyRoute } from '@/helpers/state-helper';
import { Domain, detectDomain, handleSubdomainSwitch } from '@/routing/helpers/subdomain-helper';

// stores
import { createPiniaStore, STORE_NAMESPACE } from './piniaStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useFooterStore } from '@/stores/pinia/useFooterStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { getUserCustomToken } from '@/helpers/user-helper';

if (process.env.NODE_ENV !== 'production') {
	const originalWarn = console.warn;
	console.warn = (...args) => {
		if (args[0]?.includes?.('Write operation failed')) {
			console.trace('Vue readonly computed write:');
		}
		originalWarn(...args);
	};
}

Vue.config.productionTip = process.env.NODE_ENV !== 'production';
Vue.config.performance = process.env.NODE_ENV === 'production';

Vue.config.errorHandler = (error, _, info) => {
	// mObs - next steps: sentry may not be initialized yet here, consider a queue system to avoid lost errors
	captureMessageForSentry(
		'[Uncaught Error]:',
		{ where: '[main.ssr.ts]: Vue Error Handler', error, meta: { info } },
		'error'
	);
};

global.IS_TOUCH = process.client ? !window.matchMedia('(hover: hover)').matches : false;

Vue.use(GlobalComponentsPlugin);
Vue.use(GlobalDirectivesPlugin);
Vue.use(PiniaVuePlugin);
Vue.use(VueFinalModal);
Vue.use(VueMeta, {});
Vue.filter('Thumbor', createThumborFilter());

Vue.component('NoSSR', NoSSR);
Vue.component('LazyHydrate', LazyHydrate);
Vue.component('AppFooter', AppFooter); // AppFooter global to avoid CLS
Vue.component('AppContent', AppContent);
Vue.component('ClickoutAnchor', ClickoutAnchor);

// START: CLS Check
if (process.env.NODE_ENV !== 'production') {
	import('@/plugins/cls-check').then(module => Vue.use(module.ClsCheckPlugin));
}
// END: CLS Check

// constantly check `window.hydrationBailed` for hydration issues
async function checkHydrationBails() {
	if ('hydrationBailed' in window && Array.isArray(window.hydrationBailed) && window.hydrationBailed.length > 0) {
		const bails = [...window.hydrationBailed] as { cause: string; where: string; detail: { parent: any } }[];
		window.hydrationBailed = [];
		// fire hydration bails to sentry
		for (const bail of bails) {
			const { cause, where, detail } = bail;
			const title = where
				.split(' ')
				// only take last 3 portions of css selectors (-5 because " > " are also present)
				.slice(-5)
				// remove :nth-child() from selector to better group issues
				.map(item => item.split(':')[0])
				.join(' ');
			const error = new Error(cause);
			// Vnodes have additional information attached to the Vue component
			const parentComponentName = ((detail as any).VNodes?.[0]?.context?.$vnode.tag || 'none')
				.split('-')
				.slice(-1)
				.join('');
			error.name = `[HydrationBail] ${parentComponentName} ${title}`;
			console.error('[HydrationBail]', { cause, where, detail, parent: parentComponentName });
			TrackingHelper.trackEvent('performance_metrics', {
				action: 'measure',
				property: 'hydration',
				label: `${parentComponentName}###${where}`,
				nonInteraction: true,
			});
			captureMessageForSentry(`[HydrationBail]`, { error, where, parent: parentComponentName }, 'error');
		}
	}
}

if (process.client) {
	setInterval(checkHydrationBails, 1000);
}

const MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 Days
const JWID_COOKIE = 'jw_id' as const;

/**
 * Generates a valid JustWatch ID if needed and sets
 * it in a cookie for the rest of the app to use.
 *
 * Returns true a JW ID was generated. Meaning this
 * is an SEO user.
 */
function initJwId(context: Context): boolean {
	if (process.server) {
		if (JWID_COOKIE in context.req.cookies) return false;

		const userCookie = context.req.cookies['jw_user'] || context.req.cookies['jw/user'] || '';
		if (!userCookie) return true;

		return !userCookie.includes('jwLoginId') && !userCookie.includes('jwId');
	}

	if (document.cookie.includes(JWID_COOKIE)) return false;

	const jwId = generateJwId();

	fetch(JW_CONFIG.GRAPHQL_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			query: `mutation RegisterDeviceId { registerDeviceId(input: "${jwId}") { deviceId } }`,
		}),
	})
		.then(resp => (resp?.ok ? null : resp.json()))
		.then(json => {
			const error = (json?.errors ?? [])[0];
			if (error) throw error;
		})
		.catch(error => {
			import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) => {
				captureMessageForSentry(
					'[Fetch GraphQL RegisterDeviceId]:',
					{ error, where: '[main.common.ts]: initJwId' },
					'error'
				);
			});
		});

	// Delete any existing subdomain-specific jw_id cookie first
	document.cookie = `${JWID_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; SameSite=None`;

	// Set domain to share jw_id cookie across all subdomains (www, guides, sports, etc.)
	// In development, omit domain parameter for localhost compatibility
	const domainParam = process.env.NODE_ENV === 'development' ? '' : `.${JW_CONFIG.DOMAIN}`;

	document.cookie =
		`${JWID_COOKIE}=${jwId}` +
		`; expires=${new Date(+new Date() + MAX_AGE).toUTCString()}` +
		`; path=/; secure; SameSite=None; Domain=${domainParam}`;

	return true;
}

export default async (context: Context) => {
	const t = (): bigint | undefined => process.hrtime?.bigint();
	// ssrProfile is initialized by ssr-profiler.js plugin — plugin context maps to context.ssr
	const ssrProfile = context.ssr?.ssrProfile;
	const phase = (label: string, start?: bigint) => {
		if (start != null && ssrProfile) {
			ssrProfile.timings[label] = Number(process.hrtime!.bigint() - start) / 1e6;
		}
	};

	let s = t();
	const didGenerateId = initJwId(context);
	phase('initJwId', s);

	// pass context down to App instance and all Components
	Vue.prototype.$context = context;

	try {
		const firebaseConfig = {
			apiKey: JW_CONFIG.FIREBASE_API_KEY,
			authDomain: JW_CONFIG.FIREBASE_AUTH_DOMAIN,
			projectId: JW_CONFIG.FIREBASE_PROJECT_ID,
			storageBucket: JW_CONFIG.FIREBASE_STORAGE_BUCKET,
			messagingSenderId: JW_CONFIG.FIREBASE_MESSAGING_SENDER_ID,
			appId: JW_CONFIG.FIREBASE_APP_ID,
		};
		s = t();
		initializeApp(firebaseConfig);
		phase('initFirebase', s);
	} catch (err) {
		const error: any = err;
		captureMessageForSentry('Firebase Init Issue:', { error, where: '[main.common.ts] initFirebase()' }, 'error');
	}

	if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
		// Make basic SSR init before init Store
		context.ssrData = {
			isSeoUser: didGenerateId,
			xServeTrack: null,
		};

		if (process.server) {
			// get canary flag, e.g. canary-alpha if present
			const xServeTrack = context.req.headers['x-serve-track'];
			context.ssrData.xServeTrack = xServeTrack || null;
		}
	}

	s = t();
	const i18n = createI18n();
	phase('createI18n', s);
	context.i18n = i18n;

	s = t();
	const pinia = await createPiniaStore(context);
	phase('createPiniaStore', s);
	context.pinia = pinia;

	s = t();
	const userStore = useUserStore(context.pinia);
	const routingStore = useRoutingStore(context.pinia);
	const appStore = useAppStore(context.pinia);
	const constantStore = useConstantStore(context.pinia);
	const filterStore = useFilterStore(context.pinia);
	const languageStore = useLanguageStore(context.pinia);
	const trackingStore = useTrackingStore(context.pinia);
	phase('useStores', s);

	if (process.client) {
		await storeUserGeoLocation();
		storeSrDebuggerInfo();

		// temp solution to set debug params in session storage in meta.store
		storeNoIndexDebug();
	}

	const url = context.url ?? '';
	const path = url.split('?')[0];
	const slug = /^\/[\w\d]+/.exec(url)?.[0] || '';
	const isGlobalRoute = globalRoutes.includes(path) || globalRoutes.includes(slug);

	if (isGlobalRoute) {
		let locale = userStore.persistedWebLocale || ((process.client && languageStore.detectedWebLocale) as string);

		// For Global routes we still need to know the location in the server
		// if server OR if SPA app
		if (!locale && process.server) {
			// This header is only meant for global routes because the region is not in the URL
			// IN SSR, DO NOT PASS THIS TO CLIENT (use `getUserLocationCountryCode` instead)
			const region = context.req.headers['x-client-geo-region'];
			locale = region?.toLowerCase();
		}

		// get Browser Locale as last resort
		// happens for SPA visitors that do not pass through SSR first
		if (!locale && process.client) {
			const { getUserLocationCountryCode } = await import('@/helpers/geo-location-helper');
			locale = getUserLocationCountryCode()?.toLowerCase() as WebLocale;
		}

		// hard fallback to US just in case
		if (!locale) locale = 'us';

		languageStore.setDetectedWebLocale(locale);
	} else {
		const locale = path && path.length >= 3 && path.split('/')[1];

		const webLocale = locale && COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;

		languageStore.setDetectedWebLocale(webLocale as WebLocale);
	}

	context.webLocale = languageStore.webLocale;

	if (process.server && context.req) {
		const rawHeaders = context.req.rawHeaders;
		const headers = context.req.headers;

		// Extract site header value from rawHeaders array
		let siteHeaderValue = '';
		for (let i = 0; i < rawHeaders.length; i += 2) {
			if (rawHeaders[i].toLowerCase() === 'site') {
				siteHeaderValue = rawHeaders[i + 1];
				break;
			}
		}

		// Extract Host header value from rawHeaders array
		let hostHeaderValue = '';
		for (let i = 0; i < rawHeaders.length; i += 2) {
			if (rawHeaders[i].toLowerCase() === 'host') {
				hostHeaderValue = rawHeaders[i + 1];
				break;
			}
		}

		if (!hostHeaderValue && headers.host) {
			hostHeaderValue = headers.host;
		}

		let hostname =
			siteHeaderValue || hostHeaderValue || headers['x-forwarded-host'] || headers['x-original-host'] || '';

		if (!hostname && context.req.url) {
			if (headers.referer) {
				try {
					const refUrl = new URL(headers.referer);
					hostname = refUrl.hostname;
				} catch (e) {}
			}

			// Fallback: check if we're being crawled by a bot that might include domain info in User-Agent or other headers
			if (!hostname) {
				const userAgent = headers['user-agent'] || '';

				if (userAgent.toLowerCase().includes('bot')) {
					// Check if referer contains any known subdomain pattern
					const referer = headers.referer || '';
					const knownSubdomains = Object.values(Domain).filter(domain => domain !== Domain.MAIN);

					// Look for any known subdomain in the referer
					const matchedSubdomain = knownSubdomains.find(subdomain => referer.includes(`${subdomain}.`));

					if (matchedSubdomain) {
						hostname = `${matchedSubdomain}.${JW_CONFIG.DOMAIN}`;
					} else {
						hostname = `www.${JW_CONFIG.DOMAIN}`;
					}
				}
			}
		}

		routingStore.setActiveDomain(detectDomain(hostname));
	} else if (process.client) {
		routingStore.setActiveDomain(detectDomain(window.location.hostname));
	}

	// Pass the currentDomain directly to createRouter
	s = t();
	const router = createRouter(context.webLocale, routingStore.activeDomain);
	phase('createRouter', s);
	context.router = router;

	// On server, pre-set activeRoute from the URL so that SSR queries
	// gated on activeRoute have the correct value during render.
	// This matches what Vuex did implicitly through initialization order.
	if (process.server) {
		const matchedRoute = router.match(context.url);
		if (matchedRoute?.name) {
			routingStore.setActiveRoute({ activeRoute: matchedRoute });
		}
	}

	// Initialize Sentry early with router to catch all initialization errors
	if (process.client) {
		import('@/helpers/sentry-helper').then(({ initSentry }) =>
			initSentry({
				router,
				jwId: userStore.jwId,
				xServeTrack: appStore.xServeTrack ?? null,
				isSeoUser: context?.ssrData?.isSeoUser,
				isLoggedIn: userStore.isLoggedIn,
				buyboxView: userStore.newBuyboxView,
				buyboxExpView: userStore.buyboxExpView,
				buyboxPresentationType: userStore.buyboxPresentationType ?? undefined,
				buyboxTooltipAcknowledged: userStore.newBuyboxTooltipAcknowledged,
				country: languageStore.country,
				newBuyboxFilterType: userStore.newBuyboxFilterType,
			})
		);
	}

	// Monitor JS bundle sizes for Googlebot 2MB limit
	if (process.client) {
		import('@/helpers/bundle-size-monitor').then(({ initBundleSizeMonitor }) => initBundleSizeMonitor());
	}

	// Make current domain available to all Vue components for SmartLink domain detection
	Vue.prototype.$domain = routingStore.activeDomain;

	s = t();
	await languageStore.initLanguage();
	context.language = languageStore.overriddenLanguage;
	phase('initLanguage', s);

	TrackingHelper.resetState();

	if (process.client) {
		// jwId client handover needs to happen before we await for refreshTokenPromise,
		// because if there is an error with the refresh token, it might await endlessly
		const jwId =
			// If we already have a jwId in the store (e.g., synced from cookies on subdomains)
			// then it is the most up to date
			userStore.jwId ??
			// otherwise check the cookie as the cross-domain source of truth
			Cookies.get(JWID_COOKIE) ??
			// finally fallback to localStorage
			JSON.parse(localStorage.getItem('jw/user') ?? 'null')?.jwId;

		userStore.setJwId(jwId);

		if (userStore.isLoggedIn) {
			// Wait for the refreshToken promise to finish Apollo init (it needs a fresh access token).
			// This order is important, we want to try refresh the token before initializing the store
			// and making the requests to loadList and loadSettings. The user would be logged out if the refreshToken fails
			const token = await userStore.refreshToken();

			// If token isn't returned then there was an error returning token
			if (!token) {
				context.showRefreshTokenError = true;

				TrackingHelper.trackEvent('refresh_token_error', {
					action: 'main_ssr_refresh',
				});
			}
		}

		if (userStore.accessToken) {
			const { currentUser } = await import('@/helpers/user-helper');

			const user = await currentUser();
			if (user == null) {
				const accessToken = userStore.accessToken;
				const jwId = userStore.jwId as string;

				let allRetriesFailed = false;
				let finalError: Error | undefined;

				const maxAttempts = 3;

				await withRetry(
					async () => getUserCustomToken(accessToken, jwId),
					async ({ attempt, maxAttempts, error }) => {
						const message =
							error == null
								? `[main.ssr.ts] getUserCustomToken returned undefined (attempt ${attempt}/${maxAttempts})`
								: `[main.ssr.ts] getUserCustomToken threw error (attempt ${attempt}/${maxAttempts})`;

						captureMessageForSentry(
							message,
							{
								error,
								accessToken: accessToken?.substring(0, 20) + '...',
								jwId,
								where: 'main.ssr.ts initFirebaseUser',
							},
							attempt >= maxAttempts ? 'error' : 'warning'
						);

						if (attempt >= maxAttempts) {
							allRetriesFailed = true;
							finalError = error;
						}
					},
					{ maxAttempts }
				);

				if (allRetriesFailed) {
					captureMessageForSentry(
						'[main.ssr.ts] Failed to establish Firebase session after retries - logging out',
						{
							error: finalError,
							jwLoginId: userStore.jwLoginId,
							attempts: maxAttempts,
							where: 'main.ssr.ts initFirebaseUser',
						},
						'error'
					);

					await userStore.logout();
				}
			}
		}
	}

	/**
	 * Backend Tracking
	 * Forward HTTP request user-agent to apollo requests
	 * Used for Tracking Mutations
	 **/
	const extraHeaders: Record<string, any> = {};
	if (process.server) {
		const UserAgent = context?.req?.headers['user-agent'];
		if (UserAgent && UserAgent.indexOf('GoogleStackdriverMonitoring-UptimeChecks') === -1) {
			extraHeaders['x-forwarded-user-agent'] = UserAgent;
		}

		const contextXServeTrack = context?.ssrData?.xServeTrack;
		if (contextXServeTrack) {
			extraHeaders['x-environment'] = contextXServeTrack;
		}
	}

	if (process.client && appStore.xServeTrack) {
		extraHeaders['x-environment'] = appStore.xServeTrack;
	}

	/***** Vue Apollo init *****/
	s = t();
	const apolloProvider = await getApolloProvider({
		headers: {
			...context.ssr?.data?.SERVER_ENV?.customHeaders,
			...extraHeaders,
		},
		pinia: context.pinia,
	});
	phase('getApolloProvider', s);
	/***** Vue Apollo init *****/

	if (BUILD_CONFIG.globals.BUILD_TARGET === 'SPA') {
		// initialize id service
		if (userStore.jwId) {
			context.ssrData = { isSeoUser: false };
			userStore.setJwId(userStore.jwId);
		} else {
			context.ssrData = { isSeoUser: true };
			await userStore.fetchJwId(apolloProvider);
		}
	}

	/***** SERVER ENV *****/
	if (process.server) {
		// clear SERVER_ENV to avoid leaks
		delete context.ssr.data.SERVER_ENV;
	}
	/***** SERVER ENV *****/

	appStore.setSeoUser(context.ssrData.isSeoUser);

	if (process.server) {
		// xServeTrack is only available on the server
		// context.ssrData is overwritten by the client
		appStore.setXServeTrack(context.ssrData.xServeTrack);
	}

	// NOTE(valerio.mazza): failsafe for useFilterStore to remove after a proper refactor of the useFilterStore
	if (filterStore.getWebLocale(context.webLocale) == null) {
		// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
		filterStore.initFiltersPerLocale(context.webLocale);
	}

	if (userStore.jwId && !url.includes('/refresh')) {
		s = t();
		await userStore.loadSettings();
		phase('loadSettings', s);
	}

	// all requests that are used on pretty much all pages and are un-personalised and cacheable
	// making sure that we got all uncritical requests done before we ship HTML back
	const uncriticalPromises: Promise<any>[] = [];
	if (process.server || !process.ssr) {
		const footerStore = useFooterStore(context.pinia);
		uncriticalPromises.push(footerStore.fetchTopTitles());
		uncriticalPromises.push(footerStore.fetchTopArticles());
	}

	// check for first routing and possibly update store state from url (e.g. filters from url)
	context.router.beforeEach(async (to: Route, from: Route, next: () => void) => {
		const guardStart = process.hrtime?.bigint();

		const firstRouting = from?.name === null || from?.name === 'app.refreshtoken';

		// Handle route changes that switch subdomain
		if (process.client && to.name) {
			await handleSubdomainSwitch(context, from, to);
		}

		// persist scroll position for each tab
		if (!firstRouting && process.client) {
			const tab = from?.meta?.tab === 'sports' ? `${from?.meta?.tab}-${from?.meta?.data}` : from?.meta?.tab;

			routingStore.setScrollPosition({ tab, y: window.scrollY });
		}
		if (firstRouting && process.client) {
			initArbitrage(to, languageStore.country);
		}

		// fire doEveryPageView only when:
		// - on the server
		// OR
		// - is not first routing `!firstRouting`
		// OR
		// - (first routing `firstRouting` AND activeRoute hasn't been set by the server `!routingStore.activeRoute`)
		// OR
		// - is SPA app BUILD_CONFIG.globals.BUILD_TARGET === 'SPA'

		if (
			process.server ||
			!firstRouting ||
			(firstRouting && !routingStore.activeRoute) ||
			BUILD_CONFIG.globals.BUILD_TARGET === 'SPA'
		) {
			// temporary fix for dev env @to-do fix server
			const isRefresh = to.path.indexOf('/refresh') === 0;
			const isBadAnon = from?.name !== 'app.refreshtoken' && !to.name && to.path !== '/';
			if (!isRefresh && !isBadAnon) {
				// @note: setActiveRoute needs to happen before fetchUrlMetadata
				// maybe it's better to merge these 2, since they aren't called anywhere else, either
				routingStore.setActiveRoute({ activeRoute: to });
				// we should tie pagesVisited to activeRoute.
				// with a flaky connection there is a chance that we transitioned to a new route (activeRoute),
				// but the pagesVisited hasn't reflected that because it's still awaiting urlMetadata.
				trackingStore.increasePagesVisited();

				// Home Page on firstRouting has from.path = '/' and to.path = '/', trackPage awaits urlMetadataPromise - if we dont dispatch routing/fetchUrlMetadata here this promise never resolves
				const firstRouting = from?.name === null || from?.name === 'app.refreshtoken';
				if (to.path !== from?.path || firstRouting) {
					const payload = apolloProvider ? { url: to.path, apolloProvider } : to.path;

					const fetchPromise = routingStore.fetchUrlMetadata(payload);
					// we only need the server to await the promise, the client can move forward already
					if (process.server) {
						await fetchPromise;

						if (ssrProfile && guardStart) {
							ssrProfile.timings.fetchUrlMetadata = Number(process.hrtime!.bigint() - guardStart) / 1e6;
						}
					}
				}
			}
		} else if (process.client && firstRouting) {
			// Hydration path (cached *or* fresh SSR):
			// On the very first client render we skip doEveryPageView,
			// because the server already fetched and serialized the metadata into __DATA__.
			// Note: the serialized urlMetadataPromise itself is not a real Promise
			// (it becomes just a plain object in __DATA__), so awaiting it would hang indefinitely.
			// To prevent that, we replace it with either:
			//   - Promise.resolve() if metadata for this path is already present,
			//   - or a fresh fetchUrlMetadata() call as a fallback if data is missing/stale.

			const haveMetaForPath = routingStore?.activeRoute?.path === to.path && !!routingStore?.urlMetadata;

			if (haveMetaForPath) {
				routingStore.setUrlMetadataPromise(Promise.resolve());
			} else {
				// fallback: metadata missing/stale – fetch now
				if (apolloProvider) {
					routingStore.fetchUrlMetadata({ url: to.path, apolloProvider });
				} else {
					routingStore.fetchUrlMetadata(to.path);
				}
			}
		}

		// Track page view on client only
		if (process.client) {
			const pageViewUUID = generateUUIDv4();
			trackingStore.setPageViewUUID(pageViewUUID);

			const { meta, fullPath } = to;

			// ###### TRACK PAGE ######
			// We deliberately do not `await trackPage` here, to avoid blocking the router
			// on slow async work. Instead, we ensure critical prerequisites (like urlMetadata)
			// are ready before calling it.
			// Here the async actions that happen in trackPage, that are already resolved beforehand and don't yield additional awaits:
			// - urlMetadata: resolved
			// - getContexts: only unresolved if there is an unknown pagetype
			const metaContexts = (meta?.contexts || []) as string[];

			// we track page views ourselves for title detail pages and (nearly all) sport pages
			const sportPageExceptions = [
				// 'app.sports.subdomain.all', // this isn't included because it doesn't have any sport context
				'app.sports.subdomain.overview',
				'app.sports.subdomain.competition', // @todo implement with sport context
				'app.sports.subdomain.event',
				'app.sports.subdomain.team|competition',
			];

			//handle first routing subgenre in routingConfig, assign permanentAudienceSubgenre in beforeEnter() subgenre route
			const firstRoutingIsSubgenre =
				(from?.name === null || from?.name === 'app.refreshtoken') && (to.name || '').includes('subgenre');

			if (
				!metaContexts.includes('title') &&
				!sportPageExceptions.includes(to.name || '') &&
				!firstRoutingIsSubgenre
			) {
				TrackingHelper.trackPage(fullPath);
			}
		}

		// check first page view
		if ((firstRouting && process.server) || (process.client && to.path !== from.path)) {
			const applyStart = process.hrtime?.bigint();
			// @todo translate route parameters to filters if there are any
			await applyRoute(to, context);
			if (ssrProfile && applyStart) {
				ssrProfile.timings.applyRoute = Number(process.hrtime!.bigint() - applyStart) / 1e6;
			}
		}

		next();
	});

	if (!userStore.onboardingStatus) {
		s = t();
		const { loadOnBoardingStatus } = await import('@/helpers/onboarding-helper');
		await loadOnBoardingStatus(context.webLocale, context.url);
		phase('loadOnBoardingStatus', s);
	}

	if (process.client) {
		if (LocalForage.supports(LocalForage.LOCALSTORAGE)) {
			const driver = LocalForage.LOCALSTORAGE;
			LocalForage.config({ name: STORE_NAMESPACE, driver });
		} else {
			throw new Error(`[Local storage] local forage drivers not available`);
		}
	}

	if (process.server || !process.ssr) {
		const parallelRequests: Promise<any>[] = [constantStore.fetchPackages(), constantStore.fetchConstants()];

		if (activeSubgenreCountriesWebLocales.includes(languageStore.webLocale)) {
			parallelRequests.push(constantStore.fetchSubgenres());
		}

		s = t();
		await Promise.all(parallelRequests);
		phase('parallelRequests', s);
	}

	s = t();
	context.app = new Vue({
		router,
		i18n,
		pinia,
		apolloProvider,
		render: (h: CreateElement) => h(App),
	});
	phase('newVue', s);

	// Initialize VFM helper with the $vfm instance
	if (context.app.$vfm) {
		initVfm(context.app.$vfm);
	}

	s = t();
	await Promise.all(uncriticalPromises);
	phase('fetchFooter', s);

	// Bridge webLocale to plugin context — it's only set on the app context,
	// but ssr-profiler.js afterResponse reads from the request context (context.ssr)
	if (ssrProfile) {
		ssrProfile.webLocale = context.webLocale || '';
	}

	return context;
};
